import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";

export const OFFICE = ["admin", "office", "super_admin"];
export const STATUSES = { planned: "Planned", in_progress: "In progress", on_hold: "On hold", completed: "Completed" };
export const money = value => value == null || value === "" || !Number.isFinite(Number(value)) ? "Not recorded" : new Intl.NumberFormat("en-NZ", { style: "currency", currency: "NZD" }).format(value);

// Read-only online transport. A key mismatch hides old data during the render
// preceding effect cleanup; late requests cannot populate another resource.
export function useJobRead(url, params = {}) {
  const { user } = useAuth();
  const [version, setVersion] = useState(0);
  const key = JSON.stringify([url, params, user?.org_id, user?.id, user?.role, version]);
  const [state, setState] = useState(null);
  useEffect(() => {
    if (!url) return;
    const controller = new AbortController();
    api.get(url, { params, signal: controller.signal, __flSkipReadCache: true }).then(({ data, offline }) => {
      if (controller.signal.aborted) return;
      if (offline || (data?.org_id && data.org_id !== user.org_id)) throw new Error("Workspace identity changed");
      setState({ key, data });
    }).catch(() => { if (!controller.signal.aborted) setState({ key, error: true }); });
    return () => controller.abort();
    // The serialized key includes all request and identity inputs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return { ...(state?.key === key ? state : {}), retry: () => setVersion(v => v + 1) };
}
export function ReadState({ resource, children }) {
  if (resource.error) return <div role="alert" className="py-4 text-sm">This section is unavailable. <Button variant="outline" size="sm" onClick={resource.retry}>Retry</Button></div>;
  if (!resource.data) return <div role="status" className="py-5 text-sm text-muted-foreground"><span className="block h-12 rounded-md bg-muted animate-pulse" /><span className="sr-only">Loading…</span></div>;
  return children(resource.data);
}
export function Panel({ title, action, children }) {
  return <section className="min-w-0 rounded-lg border border-border bg-card p-4 sm:p-5 text-card-foreground shadow-sm">
    <div className="mb-4 flex flex-wrap items-center justify-between gap-3"><h2 className="text-base font-semibold">{title}</h2>{action}</div>{children}
  </section>;
}
export function JobLink({ to, children }) { return <Link className="inline-flex min-h-11 items-center text-sm font-medium underline-offset-4 hover:underline" to={to}>{children}</Link>; }
export function Pager({ data, onPage }) {
  return <div className="mt-4 flex flex-wrap items-center justify-between gap-3 text-sm text-muted-foreground">
    <span>{data.total} records · Page {data.page} of {data.total_pages}</span>
    <div className="flex gap-2"><Button variant="outline" disabled={data.page <= 1} onClick={() => onPage(data.page - 1)}>Previous</Button><Button variant="outline" disabled={data.page >= data.total_pages} onClick={() => onPage(data.page + 1)}>Next</Button></div>
  </div>;
}
export function Status({ value }) { return <span className="inline-flex rounded-md bg-muted px-2 py-1 text-xs font-medium">{STATUSES[value] || String(value || "Not recorded").replaceAll("_", " ")}</span>; }
